import React from 'react';
import styled from 'styled-components';
import { themeColor, mobileMediaQuery } from '../../Theming';
import { useIsIPad } from '../../Common/Analytics/utils';
import { blueLightCardImages } from '../../Theming/images';

const GRADIENT = 'linear-gradient(0.87deg, #001B80 0.58%, #1A49FF 75.82%)';

const Container = styled.div<{ height?: number; isIPad?: boolean }>`
  height: ${({ height, isIPad }) => {
    if (height) return isIPad ? `${height + 40}px` : `${height}px`;
    return isIPad ? '330px' : '190px';
  }};
  width: 100vw;
  background: ${GRADIENT};
  position: relative;
  overflow: visible;
  margin-bottom: 50px;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -44%;
    width: ${({ isIPad }) => (isIPad ? '400%' : '265%')};
    aspect-ratio: 1;
    background: ${GRADIENT};
    border-radius: 50%;
    transform: translateX(-50%);
    z-index: 1;
  }
`;

const StarImage = styled.img<{ starTopPosition?: string; isIPad?: boolean }>`
  position: absolute;
  right: 0px;
  top: ${({ starTopPosition, isIPad }) => {
    if (starTopPosition) {
      return isIPad ? `calc(${starTopPosition} - 55px)` : starTopPosition;
    }
    return isIPad ? 'calc(100% - 35px)' : '100%';
  }};
  transform: translateY(-50%);
  height: auto;
  z-index: 9;
  width: ${({ isIPad }) => (isIPad ? '250px' : '150px')};
`;

const MainContent = styled.div<{
  logoWidth?: number;
  androidAccessibility?: boolean;
  isIPad?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: relative;
  z-index: 10;
  text-align: center;
  padding-top: ${({ isIPad }) => (isIPad ? '80px' : '60px')};
`;

const Heading = styled.div<{ headingMarginTop?: number; isIPad?: boolean }>`
  width: 100%;
  height: auto;
  padding: 20px 30px;
  margin-top: ${({ headingMarginTop }) => headingMarginTop ?? 0}px;

  & > p {
    font-family: 'Museo Sans', sans-serif;
    font-style: normal;
    font-size: ${({ isIPad }) => (isIPad ? '40px' : '32px')};
    line-height: 120%;
    letter-spacing: -0.01em;
    text-align: center;
    color: ${themeColor('white')};
    margin: 0;
    padding: 0;
    position: relative;
    z-index: 10;
  }
`;

const Subtitle = styled.div<{ isIPad?: boolean }>`
  width: 100%;
  height: auto;

  & > p {
    font-family: 'Museo Sans', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: ${({ isIPad }) => (isIPad ? '20px' : '16px')};
    line-height: 120%;
    letter-spacing: 0;
    text-align: center;
    vertical-align: middle;
    color: ${themeColor('white')};
    margin: 0 auto 0 auto;
    padding: 0 30px;
    max-width: 80%;
    position: relative;
    z-index: 10;
    width: 100%;
  }
`;

interface Props {
  showBrands?: boolean;
  logoWidth?: number;
  showFAQ?: boolean;
  heading?: React.ReactNode;
  subtitle?: React.ReactNode;
  height?: number;
  androidAccessibility?: boolean;
  starTopPosition?: string;
  headingMarginTop?: number;
}
const Header = ({
  showBrands,
  logoWidth,
  showFAQ,
  heading,
  subtitle,
  height,
  androidAccessibility,
  starTopPosition,
  headingMarginTop,
}: Props) => {
  const isIPad = useIsIPad();

  return (
    <Container height={height} isIPad={isIPad}>
      <MainContent
        logoWidth={logoWidth}
        androidAccessibility={androidAccessibility}
        isIPad={isIPad}
      >
        <Heading headingMarginTop={headingMarginTop} isIPad={isIPad}>
          {heading}
        </Heading>
        <Subtitle isIPad={isIPad}>{subtitle}</Subtitle>
      </MainContent>
      <StarImage
        isIPad={isIPad}
        src={blueLightCardImages.header_star}
        alt="Star icon"
        starTopPosition={starTopPosition}
      />
    </Container>
  );
};

export default Header;
